import React, { useState, useEffect } from 'react';
import UserDashboardLayout from '../../components/dashboard/UserDashboardLayout';
import { useAuth } from '../../context/AuthContext';
import api, { API_BASE } from '../../utils/api';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';

interface Unit {
    _id?: string;
    project: string;
    tower: string;
    floor: string;
    unitNumber: string;
    unitType: string;
    area: string;
    price: number;
    status: 'Available' | 'Hold' | 'Booked' | 'Sold' | 'Blocked';
    customerName?: string;
    customerMobile?: string;
    notes?: string;
}

interface Project {
    _id: string;
    name: string;
}

export default function DevInventory() {
    const { token } = useAuth();
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState('');
    const [units, setUnits] = useState<Unit[]>([]);
    const [loading, setLoading] = useState(false);

    // Modal states
    const [showModal, setShowModal] = useState(false);
    const [showGenerator, setShowGenerator] = useState(false);
    const [editingUnit, setEditingUnit] = useState<Partial<Unit>>({});
    const [genData, setGenData] = useState({
        tower: '',
        startFloor: '1',
        endFloor: '5',
        unitsPerFloor: '4',
        unitType: '2 BHK',
        area: '',
        price: 0
    });

    useEffect(() => {
        if (token) {
            api.getMyProjects(token).then(setProjects).catch(console.error);
        }
    }, [token]);

    useEffect(() => {
        if (selectedProject) {
            fetchUnits();
        }
    }, [selectedProject]);

    const fetchUnits = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/inventory/${selectedProject}`);
            const data = await res.json();
            setUnits(data);
        } catch (err) {
            toast.error('Failed to load project inventory matrix');
        } finally {
            setLoading(false);
        }
    };

    const handleEditUnit = (unit: Unit) => {
        setEditingUnit(unit);
        setShowModal(true);
    };

    const handleSaveUnit = async () => {
        try {
            const res = await fetch(`${API_BASE}/inventory/update`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(editingUnit)
            });

            if (res.ok) {
                toast.success('Inventory state synchronized');
                setShowModal(false);
                fetchUnits();
            } else {
                toast.error('Protocol synchronization failed');
            }
        } catch (err) {
            toast.error('Network link failure');
        }
    };

    const handleGenerateUnits = async () => {
        if (!selectedProject) return toast.error("Selection protocol required: Select a project node.");
        if (!genData.tower) return toast.error("Tower identification required.");

        try {
            const res = await fetch(`${API_BASE}/inventory/bulk-generate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ ...genData, project: selectedProject })
            });

            const data = await res.json();
            if (res.ok) {
                toast.success(data.message);
                setShowGenerator(false);
                fetchUnits();
            } else {
                toast.error(data.message || 'Generation sequence failed');
            }
        } catch (err) {
            toast.error('Link failure during generation');
        }
    };

    const statusConfig: Record<string, { bg: string, text: string, ring: string, lightBg: string }> = {
        Available: { bg: 'bg-emerald-500', text: 'text-emerald-500', ring: 'ring-emerald-500/20', lightBg: 'bg-emerald-50/80' },
        Hold: { bg: 'bg-amber-500', text: 'text-amber-500', ring: 'ring-amber-500/20', lightBg: 'bg-amber-50/80' },
        Booked: { bg: 'bg-blue-500', text: 'text-blue-500', ring: 'ring-blue-500/20', lightBg: 'bg-blue-50/80' },
        Sold: { bg: 'bg-rose-500', text: 'text-rose-500', ring: 'ring-rose-500/20', lightBg: 'bg-rose-50/80' },
        Blocked: { bg: 'bg-slate-500', text: 'text-slate-500', ring: 'ring-slate-500/20', lightBg: 'bg-slate-50/80' }
    };

    const towers = Array.from(new Set(units.map(u => u.tower))).sort();

    return (
        <UserDashboardLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                <div className="animate__animated animate__fadeInLeft">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-[#0F2A5F] flex items-center justify-center text-white shadow-lg shadow-blue-600/20">
                            <i className="fa-solid fa-boxes-stacked text-xl text-[#D4A017]"></i>
                        </div>
                        <h2 className="text-3xl font-black text-[#0F2A5F] dark:text-white leading-none tracking-tight uppercase italic">Inventory Management</h2>
                    </div>
                    <p className="text-[#0F2A5F]/70 dark:text-slate-400 font-medium tracking-tight">Manage unit availability and configuration status for your projects.</p>
                </div>
                
                <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4 animate__animated animate__fadeInRight">
                    {selectedProject && (
                        <button 
                            onClick={() => setShowGenerator(true)}
                            className="px-6 py-4 bg-[#D4A017] text-white text-[10px] font-black uppercase tracking-widest rounded-2xl shadow-lg hover:shadow-amber-500/20 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2"
                        >
                            <i className="fa-solid fa-wand-sparkles"></i>
                            Generate Matrix
                        </button>
                    )}
                    <div className="relative group min-w-[300px]">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <i className="fa-solid fa-city text-[#D4A017]"></i>
                        </div>
                        <select
                            className="block w-full pl-12 pr-10 py-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-xs font-black uppercase tracking-widest focus:ring-4 focus:ring-blue-600/10 focus:border-blue-600 dark:focus:border-blue-500 transition-all text-slate-900 dark:text-white appearance-none cursor-pointer shadow-lg"
                            value={selectedProject}
                            onChange={(e) => setSelectedProject(e.target.value)}
                        >
                            <option value="">Select Project Node</option>
                            {projects.map(p => (
                                <option key={p._id} value={p._id}>{p.name}</option>
                            ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
                            <i className="fa-solid fa-chevron-down text-[10px] text-slate-400"></i>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-[3rem] border border-slate-100 dark:border-slate-800 shadow-xl overflow-hidden min-h-[500px] animate__animated animate__fadeInUp">
                <div className="p-8 md:p-10 border-b border-slate-100 dark:border-slate-800 bg-[#0F2A5F]/5 backdrop-blur-sm">
                    <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
                        {Object.entries(statusConfig).map(([status, config]) => (
                            <div key={status} className="flex items-center gap-3 group transition-transform hover:scale-105">
                                <div className={`w-3.5 h-3.5 rounded-full ${config.bg} shadow-lg ring-2 ring-white`}></div>
                                <span className="text-[10px] font-black text-[#0F2A5F]/60 dark:text-slate-500 uppercase tracking-widest leading-none">{status}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="p-8 md:p-12">
                    {!selectedProject ? (
                        <div className="flex flex-col items-center justify-center py-20 text-center opacity-60">
                            <div className="w-24 h-24 bg-slate-50 dark:bg-slate-800/50 rounded-full flex items-center justify-center mb-6 border-2 border-dashed border-slate-200 dark:border-slate-700">
                                <i className="fa-solid fa-folder-open text-4xl text-slate-300"></i>
                            </div>
                            <h5 className="text-xl font-black text-[#0F2A5F] dark:text-white tracking-tight mb-2 uppercase italic leading-none">Awaiting Selection</h5>
                            <p className="text-[#0F2A5F]/50 dark:text-slate-500 text-sm font-medium italic">Initialize a project protocol from the selector above.</p>
                        </div>
                    ) : loading ? (
                        <div className="flex flex-col items-center justify-center py-24">
                            <div className="w-16 h-16 border-[5px] border-[#D4A017] border-t-transparent rounded-full animate-spin mb-6 shadow-2xl"></div>
                            <p className="text-[#0F2A5F]/60 font-black uppercase tracking-[0.2em] text-[10px]">Accessing Unit Matrix...</p>
                        </div>
                    ) : (
                        <div className="space-y-16">
                            {towers.length === 0 ? (
                                <div className="text-center py-20">
                                    <i className="fa-solid fa-triangle-exclamation text-4xl text-amber-500 mb-6"></i>
                                    <h4 className="text-xl font-black text-[#0F2A5F] uppercase italic">No Inventory Data Found</h4>
                                    <p className="text-[#0F2A5F]/60 mt-2">Contact administrator if you expected unit data here.</p>
                                </div>
                            ) : (
                                towers.map(tower => {
                                    const towerUnits = units.filter(u => u.tower === tower);
                                    const floors = Array.from(new Set(towerUnits.map(u => u.floor))).sort((a, b) => parseInt(b) - parseInt(a));

                                    return (
                                        <div key={tower} className="animate__animated animate__fadeIn">
                                            <div className="flex items-center gap-4 mb-8">
                                                <div className="w-10 h-10 bg-[#0F2A5F] rounded-xl flex items-center justify-center text-white shadow-lg">
                                                    <i className="fa-solid fa-building text-xs text-[#D4A017]"></i>
                                                </div>
                                                <h4 className="text-lg font-black text-[#0F2A5F] dark:text-white tracking-tight uppercase italic leading-none">Section Tower {tower}</h4>
                                                <div className="h-px bg-[#0F2A5F]/10 dark:bg-slate-800 flex-grow"></div>
                                            </div>

                                            <div className="space-y-4">
                                                {floors.map(floor => (
                                                    <div key={floor} className="flex items-center gap-6 group/row">
                                                        <div className="w-24 hidden md:block">
                                                            <span className="text-[10px] font-black text-[#0F2A5F] dark:text-[#D4A017] tracking-widest uppercase italic group-hover/row:translate-x-2 transition-transform duration-300">Floor {floor}</span>
                                                        </div>
                                                        <div className="flex-grow flex flex-wrap gap-4">
                                                            {towerUnits.filter(u => u.floor === floor).map(unit => (
                                                                <button
                                                                    key={unit._id}
                                                                    className={`relative flex flex-col items-center justify-center w-24 h-24 rounded-[1.5rem] border-2 transition-all duration-300 group/unit shadow-lg hover:z-10 hover:-translate-y-2 active:scale-95 ${statusConfig[unit.status]?.lightBg || 'bg-white'} border-transparent hover:border-[#D4A017]`}
                                                                    onClick={() => handleEditUnit(unit)}
                                                                >
                                                                    <div className={`absolute top-2 right-2 w-2 h-2 rounded-full ${statusConfig[unit.status]?.bg || 'bg-slate-500'} shadow-sm`}></div>
                                                                    <span className="text-[14px] font-black text-[#0F2A5F] leading-none tracking-tight mb-1">{unit.unitNumber}</span>
                                                                    <span className="text-[9px] font-bold text-[#0F2A5F]/60 uppercase tracking-widest mb-0.5">{unit.unitType}</span>
                                                                    <span className="text-[8px] font-black text-[#0F2A5F]/40 uppercase tracking-[0.1em]">{unit.status}</span>
                                                                    
                                                                    <div className="absolute -top-10 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-[#0F2A5F] text-white text-[8px] font-black uppercase tracking-widest rounded-lg opacity-0 group-hover/unit:opacity-100 pointer-events-none transition-all shadow-xl z-20">
                                                                        Manage Unit
                                                                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#0F2A5F]"></div>
                                                                    </div>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Premium Side Modal */}
            {showModal && (
                <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-[#0F2A5F]/40 backdrop-blur-md animate-in fade-in duration-300" onClick={() => setShowModal(false)}></div>
                    <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-500 border border-gray-100 dark:border-slate-800">
                        <div className="p-10 border-b border-gray-50 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/30">
                            <div className="flex items-center gap-5">
                                <div className="w-14 h-14 bg-[#0F2A5F] rounded-2xl flex items-center justify-center text-white shadow-xl">
                                    <i className="fa-solid fa-sliders text-xl text-[#D4A017]"></i>
                                </div>
                                <div>
                                    <h5 className="text-2xl font-black text-[#0F2A5F] dark:text-white tracking-tight uppercase italic leading-none">Unit {editingUnit.tower}-{editingUnit.unitNumber}</h5>
                                    <p className="text-[10px] font-black text-[#D4A017] uppercase tracking-widest mt-2">Configuration Node Synchronization</p>
                                </div>
                            </div>
                            <button onClick={() => setShowModal(false)} className="w-12 h-12 rounded-2xl hover:bg-white text-slate-400 hover:text-black transition-all flex items-center justify-center shadow-sm">
                                <i className="fa-solid fa-xmark text-xl"></i>
                            </button>
                        </div>

                        <div className="p-10 max-h-[70vh] overflow-y-auto scrollbar-hide">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
                                {[
                                    { label: 'Type', value: editingUnit.unitType, icon: 'fa-cubes' },
                                    { label: 'Area', value: editingUnit.area, icon: 'fa-ruler-combined' },
                                    { label: 'Value', value: editingUnit.price ? `₹${(editingUnit.price/10000000).toFixed(2)} Cr` : 'Call', icon: 'fa-tag' },
                                    { label: 'Level', value: `Floor ${editingUnit.floor}`, icon: 'fa-stairs' }
                                ].map((stat, i) => (
                                    <div key={i} className="p-5 bg-white rounded-3xl border border-gray-100 shadow-sm">
                                        <div className="flex items-center gap-2 mb-3">
                                            <i className={`fa-solid ${stat.icon} text-[11px] text-[#D4A017]`}></i>
                                            <span className="text-[9px] font-black text-[#0F2A5F]/40 uppercase tracking-widest">{stat.label}</span>
                                        </div>
                                        <div className="text-sm font-black text-[#0F2A5F] truncate italic leading-none">{stat.value || 'N/A'}</div>
                                    </div>
                                ))}
                            </div>

                            <div className="space-y-12">
                                <div>
                                    <h6 className="text-[10px] font-black text-black dark:text-white uppercase tracking-[0.3em] mb-6 flex items-center gap-3">
                                        <div className="w-2 h-4 bg-[#D4A017] rounded-full"></div>
                                        Availability State
                                    </h6>
                                    <div className="flex flex-wrap gap-3">
                                        {['Available', 'Hold', 'Booked', 'Sold', 'Blocked'].map(s => (
                                            <button
                                                key={s}
                                                onClick={() => setEditingUnit({ ...editingUnit, status: s as any })}
                                                className={`px-8 py-3.5 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${
                                                    editingUnit.status === s 
                                                    ? 'bg-[#0F2A5F] text-[#D4A017] shadow-2xl ring-4 ring-blue-600/5' 
                                                    : 'bg-gray-50 dark:bg-slate-800 text-slate-400 border border-gray-100 dark:border-slate-700 hover:border-[#D4A017]/50'
                                                }`}
                                            >
                                                {s}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <h6 className="text-[10px] font-black text-black dark:text-white uppercase tracking-[0.3em] mb-6 flex items-center gap-3">
                                        <div className="w-2 h-4 bg-[#D4A017] rounded-full"></div>
                                        Ownership Information
                                    </h6>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-3">
                                            <label className="text-[10px] font-black text-[#0F2A5F]/50 uppercase tracking-widest ml-1">Holder Name</label>
                                            <input
                                                type="text"
                                                className="block w-full px-6 py-4.5 bg-gray-50 dark:bg-slate-800 border-2 border-transparent focus:bg-white focus:border-[#D4A017] rounded-2xl text-xs font-black text-[#0F2A5F] dark:text-white transition-all outline-none"
                                                placeholder="Legal Identifier"
                                                value={editingUnit.customerName || ''}
                                                onChange={(e) => setEditingUnit({ ...editingUnit, customerName: e.target.value })}
                                            />
                                        </div>
                                        <div className="space-y-3">
                                            <label className="text-[10px] font-black text-[#0F2A5F]/50 uppercase tracking-widest ml-1">Contact Protocol</label>
                                            <input
                                                type="text"
                                                className="block w-full px-6 py-4.5 bg-gray-50 dark:bg-slate-800 border-2 border-transparent focus:bg-white focus:border-[#D4A017] rounded-2xl text-xs font-black text-[#0F2A5F] dark:text-white transition-all outline-none"
                                                placeholder="+91 00000 00000"
                                                value={editingUnit.customerMobile || ''}
                                                onChange={(e) => setEditingUnit({ ...editingUnit, customerMobile: e.target.value })}
                                            />
                                        </div>
                                        <div className="md:col-span-2 space-y-3">
                                            <label className="text-[10px] font-black text-[#0F2A5F]/50 uppercase tracking-widest ml-1">Internal Log Annotations</label>
                                            <textarea
                                                rows={4}
                                                className="block w-full px-6 py-4.5 bg-gray-50 dark:bg-slate-800 border-2 border-transparent focus:bg-white focus:border-[#D4A017] rounded-2xl text-xs font-black text-[#0F2A5F] dark:text-white transition-all outline-none resize-none"
                                                placeholder="Project intelligence notes..."
                                                value={editingUnit.notes || ''}
                                                onChange={(e) => setEditingUnit({ ...editingUnit, notes: e.target.value })}
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-10 border-t border-gray-50 dark:border-slate-800 flex items-center justify-end gap-6 bg-slate-50/50 dark:bg-slate-800/30">
                            <button
                                onClick={() => setShowModal(false)}
                                className="text-[11px] font-black uppercase tracking-widest text-slate-400 hover:text-black transition-colors"
                            >
                                Discard Changes
                            </button>
                            <button
                                onClick={handleSaveUnit}
                                className="px-12 py-5 bg-[#0F2A5F] text-[#D4A017] text-[11px] font-black uppercase tracking-widest rounded-2xl shadow-2xl hover:scale-105 active:scale-95 transition-all"
                            >
                                Synchronize Protocol
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Generator Modal */}
            {showGenerator && (
                <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-[#0F2A5F]/40 backdrop-blur-md animate-in fade-in duration-300" onClick={() => setShowGenerator(false)}></div>
                    <div className="relative w-full max-w-xl bg-white dark:bg-slate-900 rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-500 border border-gray-100 dark:border-slate-800">
                        <div className="p-10 border-b border-gray-50 dark:border-slate-800 flex items-center justify-between bg-white dark:bg-slate-900">
                            <div className="flex items-center gap-5">
                                <div className="w-14 h-14 bg-[#D4A017] rounded-2xl flex items-center justify-center text-white shadow-xl">
                                    <i className="fa-solid fa-wand-sparkles text-xl"></i>
                                </div>
                                <div>
                                    <h5 className="text-2xl font-black text-[#0F2A5F] dark:text-white tracking-tight uppercase italic leading-none">Matrix Generator</h5>
                                    <p className="text-[10px] font-black text-[#0F2A5F]/40 uppercase tracking-widest mt-2">Bulk Unit Population Protocol</p>
                                </div>
                            </div>
                            <button onClick={() => setShowGenerator(false)} className="w-12 h-12 rounded-2xl hover:bg-gray-50 text-slate-400 transition-all flex items-center justify-center">
                                <i className="fa-solid fa-xmark text-xl"></i>
                            </button>
                        </div>

                        <div className="p-10">
                            <div className="grid grid-cols-2 gap-6 mb-8">
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-[#0F2A5F]/50 uppercase tracking-widest ml-1">Tower Identification</label>
                                    <input
                                        type="text"
                                        className="block w-full px-6 py-4.5 bg-gray-50 dark:bg-slate-800 border-2 border-transparent focus:bg-white focus:border-[#D4A017] rounded-2xl text-xs font-black text-[#0F2A5F] dark:text-white transition-all outline-none"
                                        placeholder="e.g. Tower A"
                                        value={genData.tower}
                                        onChange={(e) => setGenData({ ...genData, tower: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-[#0F2A5F]/50 uppercase tracking-widest ml-1">Base Topology</label>
                                    <select
                                        className="block w-full px-6 py-4.5 bg-gray-50 dark:bg-slate-800 border-2 border-transparent focus:bg-white focus:border-[#D4A017] rounded-2xl text-xs font-black text-[#0F2A5F] dark:text-white transition-all outline-none appearance-none"
                                        value={genData.unitType}
                                        onChange={(e) => setGenData({ ...genData, unitType: e.target.value })}
                                    >
                                        <option value="1 BHK">1 BHK</option>
                                        <option value="2 BHK">2 BHK</option>
                                        <option value="3 BHK">3 BHK</option>
                                        <option value="4 BHK+">4 BHK+</option>
                                        <option value="Villa">Villa</option>
                                        <option value="Plot">Plot</option>
                                    </select>
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-[#0F2A5F]/50 uppercase tracking-widest ml-1">Start Floor</label>
                                    <input
                                        type="number"
                                        className="block w-full px-6 py-4.5 bg-gray-50 dark:bg-slate-800 border-2 border-transparent focus:bg-white focus:border-[#D4A017] rounded-2xl text-xs font-black text-[#0F2A5F] dark:text-white transition-all outline-none"
                                        value={genData.startFloor}
                                        onChange={(e) => setGenData({ ...genData, startFloor: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-[#0F2A5F]/50 uppercase tracking-widest ml-1">End Floor</label>
                                    <input
                                        type="number"
                                        className="block w-full px-6 py-4.5 bg-gray-50 dark:bg-slate-800 border-2 border-transparent focus:bg-white focus:border-[#D4A017] rounded-2xl text-xs font-black text-[#0F2A5F] dark:text-white transition-all outline-none"
                                        value={genData.endFloor}
                                        onChange={(e) => setGenData({ ...genData, endFloor: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-[#0F2A5F]/50 uppercase tracking-widest ml-1">Units Per Level</label>
                                    <input
                                        type="number"
                                        className="block w-full px-6 py-4.5 bg-gray-50 dark:bg-slate-800 border-2 border-transparent focus:bg-white focus:border-[#D4A017] rounded-2xl text-xs font-black text-[#0F2A5F] dark:text-white transition-all outline-none"
                                        value={genData.unitsPerFloor}
                                        onChange={(e) => setGenData({ ...genData, unitsPerFloor: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-[#0F2A5F]/50 uppercase tracking-widest ml-1">Target Surface (Sq.Ft)</label>
                                    <input
                                        type="text"
                                        className="block w-full px-6 py-4.5 bg-gray-50 dark:bg-slate-800 border-2 border-transparent focus:bg-white focus:border-[#D4A017] rounded-2xl text-xs font-black text-[#0F2A5F] dark:text-white transition-all outline-none"
                                        placeholder="e.g. 1250"
                                        value={genData.area}
                                        onChange={(e) => setGenData({ ...genData, area: e.target.value })}
                                    />
                                </div>
                            </div>

                            <button
                                onClick={handleGenerateUnits}
                                className="w-full py-5 bg-[#0F2A5F] text-[#D4A017] text-[11px] font-black uppercase tracking-widest rounded-2xl shadow-2xl hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-3"
                            >
                                <i className="fa-solid fa-bolt"></i>
                                Initialize Generation Sequence
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </UserDashboardLayout>
    );
}
